import React from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginPage from './auth/LoginPage';
import SignupPage from './auth/SignupPage';
import RequestResetPage from './auth/RequestResetPage';
import AuthCallback from './auth/AuthCallback';
import SuperAdminDashboard from './dashboards/SuperAdminDashboard';
import IssuerDashboard from './dashboards/IssuerDashboard';
import RecipientDashboard from './dashboards/RecipientDashboard';
import VerifierPortal from './dashboards/VerifierPortal';
import CredentialVerification from './verification/CredentialVerification';
import AnalyticsDashboard from './dashboards/AnalyticsDashboard';

// Helper function to get the correct route based on user role
const getRoleRoute = (role: string): string => {
  switch (role) {
    case 'super_admin':
    case 'admin':
      return 'admin';
    case 'issuer':
      return 'issuer';
    case 'holder':
      return 'recipient';
    case 'verifier':
      return 'verifier';
    default:
      return 'recipient';
  }
};

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Public Route Component (redirects authenticated users)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  
  if (user) {
    // Redirect to role-specific dashboard instead of generic /dashboard
    return <Navigate to={`/${getRoleRoute(user.role)}`} replace />;
  }
  
  return <>{children}</>;
};

// Wrapper component for CredentialVerification to extract credentialId from URL
const CredentialVerificationWrapper = () => {
  const { credentialId } = useParams<{ credentialId: string }>();
  
  if (!credentialId) {
    // Redirect to role-specific dashboard instead of generic /dashboard
    const { user } = useAuth();
    if (user) {
      return <Navigate to={`/${getRoleRoute(user.role)}`} replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  return <CredentialVerification credentialId={credentialId} />;
};

// Component to handle root route with potential auth callback
const RootRouteHandler = () => {
  const { user } = useAuth();
  const location = useLocation();
  
  // If user is logged in, send them to their dashboard
  if (user) {
    return <Navigate to={`/${getRoleRoute(user.role)}`} replace />;
  }
  
  // Parse the hash params to check for auth-related parameters
  const hashParams = new URLSearchParams(location.hash.replace('#', ''));
  const type = hashParams.get('type');
  const hasAccessToken = hashParams.get('access_token');
  
  // Handle different auth flows
  if (location.hash) {
    // For password reset and invite flows, redirect to signup page
    if (type === 'recovery' || type === 'invite') {
      // Convert hash parameters to search parameters for SignupPage
      const searchParams = new URLSearchParams();
      hashParams.forEach((value, key) => searchParams.append(key, value));
      return <Navigate to={`/signup?${searchParams.toString()}`} replace />;
    }
    
    // For Supabase auth callbacks with access_token
    if (hasAccessToken) {
      return <AuthCallback />;
    }
  }
  
  // Default to login page
  return <Navigate to="/login" replace />;
};

// Wrapper component for AnalyticsDashboard to pass userRole
const AnalyticsDashboardWrapper = () => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Only allow certain roles to access analytics
  if (user.role === 'super_admin' || user.role === 'admin' || user.role === 'verifier') {
    return <AnalyticsDashboard userRole={user.role} />;
  }
  
  // Redirect other roles to dashboard
  return <Navigate to="/dashboard" replace />;
};

export default function AppRouter() {
  const { user } = useAuth();
  const location = useLocation();

  // Console log the full URL whenever the route changes
  React.useEffect(() => {
    const fullUrl = `${window.location.protocol}//${window.location.host}${location.pathname}${location.search}${location.hash}`;
    console.log('🌐 Current URL:', fullUrl);
    console.log('📍 Location details:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state
    });
  }, [location]);
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      
      <Route 
        path="/signup" 
        element={<SignupPage />} 
      />
      
      <Route 
        path="/request-reset" 
        element={
          <PublicRoute>
            <RequestResetPage />
          </PublicRoute>
        } 
      />
      {/* Note: /signup handles both invite and password recovery flows via URL params */}
      
      {/* Public verification route */}
      <Route 
        path="/verify/:credentialId" 
        element={<CredentialVerificationWrapper />} 
      />
      
      {/* Role-based Dashboard Routes */}
      <Route 
        path="/recipient" 
        element={
          <ProtectedRoute>
            <RecipientDashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/issuer" 
        element={
          <ProtectedRoute>
            <IssuerDashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/verifier" 
        element={
          <ProtectedRoute>
            <VerifierPortal />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/analytics" 
        element={
          <ProtectedRoute>
            <AnalyticsDashboardWrapper />
          </ProtectedRoute>
        } 
      />
      
      {/* Root route - handles auth callbacks and normal redirects */}
      <Route 
        path="/" 
        element={<RootRouteHandler />} 
      />
      
      {/* Catch all - redirect to appropriate route */}
      <Route 
        path="*" 
        element={
          user ? <Navigate to={`/${getRoleRoute(user.role)}`} replace /> : <Navigate to="/login" replace />
        } 
      />
    </Routes>
  );
}
